import React, { useEffect, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, TextField, Button } from '@mui/material';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

// services
import { saveMenuData } from '../../../services/kitchenService';
import { useAuth } from '../../../AuthContext';
import { MENU_TYPE_OPTIONS, MENU_NAME_EXAMPLES_TEXT } from './common';
import { useLocale } from '../../../i18n/LanguageContext';

export default function MenuCreator(props) {
  const { auth } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const brandNewMenu = {name: '', type: '', isActive: false, kitchenId: props.kitchenId };
  const [menu, setMenu] = useState(brandNewMenu);
  const [fieldErrors, setFieldErrors] = useState({ name: undefined, type: undefined });
  const selectedMenuType = MENU_TYPE_OPTIONS.find((menuTypeOption) => menuTypeOption.value === menu.type);

  const validateField = (name, value) => {
    // Check if required
    if (!value || value.trim() === '') {
      return t('kitchenMenus.validation.required');
    }
    
    // Check for special characters like < and >
    if (value.includes('<') || value.includes('>')) {
      return t('kitchenMenus.validation.specialChars');
    }
    
    // Check min length
    if (value.length < 3) {
      return t('kitchenMenus.validation.minLength');
    }
    
    // Check max length
    if (value.length > 100) {
      return t('kitchenMenus.validation.maxLength');
    }
    
    // Field-specific validation
    if (name === 'type') {
      // Type should not contain any numbers
      if (/[0-9]/.test(value)) {
        return t('kitchenMenus.validation.noNumbers');
      }
    } else {
      // Name should contain at least one letter (not just numbers)
      if (!/[a-zA-Z]/.test(value)) {
        return t('kitchenMenus.validation.letterRequired');
      }
    }
    
    return undefined;
  };

  const isFormValid = () => {
    return !fieldErrors.name && !fieldErrors.type && menu.name.trim() !== '' && menu.type.trim() !== '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid()) {
      saveMenuData(menu, auth).then((menuSaved) => {
        if (menuSaved) {
          props.onMenuCreation(menuSaved);
          setOpen(false);
          setMenu(brandNewMenu);
        } else {
          setError(true);
        }
      }).catch(() => {
        setError(true);
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Block < and > characters from being typed
    if (value.includes('<') || value.includes('>')) {
      const cleanValue = value.replace(/[<>]/g, '');
      setMenu((prevMenu) => ({
        ...prevMenu,
        [name]: cleanValue,
      }));
      return;
    }
    
    setMenu((prevMenu) => ({
      ...prevMenu,
      [name]: value,
    }));
    
    // Validate on change
    const error = validateField(name, value);
    setFieldErrors(prev => ({...prev, [name]: error}));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFieldErrors(prev => ({...prev, [name]: error}));
  };

  const handleSwitchChange = (e) => {
    setMenu({...menu, isActive: e.target.checked});
  };
  
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMenu(brandNewMenu);
    setFieldErrors({ name: undefined, type: undefined });
  };

  return (
    <div>
      <Button
        onClick={handleClickOpen}
        variant="outlined"
        startIcon={<RestaurantMenuIcon />}
        sx={(theme) => ({
          textTransform: 'none',
          fontWeight: 600,
          px: 2.25,
          py: 0.9,
          borderRadius: 2,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.secondary,
          },
        })}
        data-testid="open-menu-creator-button"
      >
        {t('kitchenMenus.createButton')}
      </Button>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        { error ? (<Alert severity="error">{t('kitchenDashboard.error')}</Alert>) : ''}
        <DialogTitle id="responsive-dialog-title" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <div>{t('kitchenMenus.newMenuTitle')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="caption">{t('kitchenMenus.inactive')}</Typography>
                  <Switch 
                    name='isActive'
                    checked={menu.isActive} 
                    onChange={handleSwitchChange}
                    value={menu.isActive}
                    inputProps={{ 'aria-label': 'Menu status' }}
                  />
                  <Typography variant="caption">{t('kitchenMenus.active')}</Typography>
                </div>
                
            </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <form >
              
                    <div>
                      <TextField
                          label={t('kitchenMenus.nameLabel')}
                          name="name"
                          value={menu.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          required
                          error={!!fieldErrors.name}
                          helperText={fieldErrors.name}
                          data-testid="menu-name-input"
                      />
                      <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 0, marginBottom: 8 }} data-testid="menu-name-examples">
                        {t('kitchenMenus.examples')}
                      </Typography>
                      <TextField
                          label={t('kitchenMenus.typeLabel')}
                          name="type"
                          value={menu.type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          required
                          error={!!fieldErrors.type}
                          helperText={fieldErrors.type}
                          select
                          SelectProps={{ native: true }}
                          data-testid="menu-type-input"
                      >
                        <option value=""></option>
                        {MENU_TYPE_OPTIONS.map((menuTypeOption) => (
                          <option key={menuTypeOption.value} value={menuTypeOption.value}>
                            {t(`kitchenMenus.menuTypes.${menuTypeOption.value}`)}
                          </option>
                        ))}
                      </TextField>
                      {selectedMenuType ? (
                        <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 0 }} data-testid="menu-type-description">
                          {t(`kitchenMenus.menuTypeDescriptions.${selectedMenuType.value}`)}
                        </Typography>
                      ) : null}
                    </div>
            </form>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} style={{ textTransform: 'none'}}>
            {t('kitchenMenus.cancel')}
          </Button>
          <Button onClick={handleSubmit} autoFocus style={{ textTransform: 'none'}} variant='contained' disabled={!isFormValid()}>
            {t('kitchenMenus.saveNew')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
